import {RsnDatePicker, RsnDialog, RsnSelect, useBoolean} from '@raison/ui-kit';
import {isAfter, startOfDay, sub} from 'date-fns';
import React, {FormEvent, useState} from 'react';
import {required, useValidateForm} from '~/processes/reports/ui/hooks/useValidateForm';
import {ReportDateDialog} from '~/processes/reports/ui/ReportDateDialog';
import {AccountRequestResult} from '../api/hooks/useReportsFormsRequests';
import {AccountSchema} from '../api/schemas/accountSchema';
import {getIcon} from './utils/geticon';

export interface AccountForm {
    date?: Date;
    service: string;
}

interface Props extends AccountRequestResult {
    formId: string;
}

export default function AccountStatementReportForm({formId, ...rest}: Props) {
    const {options, values, setValue, onSubmit, getHelperProps} = useAccountForm(rest);
    const [isReportDateDialogOpen, setIsReportDateDialogOpen] = useBoolean(false);

    const yesterday = startOfDay(sub(new Date(), {days: 1}));

    const handleDateChange = (date: Date | undefined) => {
        date ??= yesterday;
        if (isAfter(date, yesterday)) {
            date = yesterday;
        }
        setValue('date')(date);
    };

    return (
        <>
            <form id={formId} onSubmit={onSubmit} className='flex flex-1 flex-col gap-6 px-4'>
                <RsnDatePicker
                    name='date'
                    label={
                        <RsnDatePicker.LabelWithInfo onInfoClick={setIsReportDateDialogOpen.on}>
                            Date
                        </RsnDatePicker.LabelWithInfo>
                    }
                    value={values.date}
                    max={yesterday}
                    onChange={handleDateChange}
                    {...getHelperProps('date')}
                />
                <RsnSelect<string>
                    name='service'
                    label={<RsnSelect.Label>Services</RsnSelect.Label>}
                    options={options.services}
                    onChange={setValue('service')}
                    value={values.service}
                />
            </form>
            <RsnDialog.Controller onClose={setIsReportDateDialogOpen.off}>
                {isReportDateDialogOpen && <ReportDateDialog />}
            </RsnDialog.Controller>
        </>
    );
}

function useAccountForm({data, mutate}: AccountRequestResult) {
    const [formState, setFormState] = useState<AccountForm>({
        date: startOfDay(sub(new Date(), {days: 1})),
        service: getServices(data)?.[0].value,
    });

    const {signalSubmitTry, signalChangeTry, getHelperProps} = useValidateForm<AccountForm>(formState, {
        date: [required('Required')],
    });

    const services = getServices(data);

    const setValue =
        (key: keyof AccountForm) =>
        <K extends keyof AccountForm>(value: AccountForm[K]) => {
            signalChangeTry(key);
            setFormState({...formState, [key]: value});
        };

    const onSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (signalSubmitTry()) {
            mutate(formState);
        }
    };

    return {
        values: formState,
        setValue,
        onSubmit,
        options: {
            services,
        },
        getHelperProps,
    };
}

function getServices(data: AccountSchema) {
    return data.services.map(({name, key, icon}) => ({
        key,
        label: name,
        value: key,
        icon: getIcon(icon),
    }));
}
